import {DayOfWeek, LocalDate} from '@js-joda/core';
import Grid from '@material-ui/core/Grid';
import useApiFetch, {apiEndpoint} from '@tracker/shared/src/Auth/Api';
import InlineLoadingIndicator from '@tracker/shared/src/LoadingIndicator/InlineLoadingIndicator';
import {usePreferencesState, useUpdatePreferences} from '@tracker/shared/src/Preferences/PreferencesProvider';
import {useProjects} from '@tracker/shared/src/Projects/ProjectsProvider';
import {favoritesTeamId, unscheduledTeamId} from "@tracker/shared/src/Scheduling/Teams";
import CapacityContextProvider from "@tracker/shared/src/Scheduling/Utils/CapacityContextProvider";
import {
    ClientServiceTeam,
    compileSchedules,
    Employee,
    EmployeeActuals,
    Project,
    Schedule
} from '@tracker/shared/src/Scheduling/Utils/EmployeeSchedulesCompiler';
import {employeeSchedulesReducer} from '@tracker/shared/src/Scheduling/Utils/EmployeeSchedulesReducer';
import {reasonsReducer} from '@tracker/shared/src/Scheduling/Utils/ReasonsReducer';
import ScheduleContextProvider from '@tracker/shared/src/Scheduling/Utils/ScheduleContextProvider';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import ProjectSchedule from './ProjectSchedule';
import {FilterSearch} from './ScheduleFilter';
import VirtualTableSchedule from './VirtualTableSchedule';

type Props = {
    startDate : LocalDate;
    filterSearch : FilterSearch;
    projects : Project[];

    employees : Employee[];
    filtersActive : boolean;
    teams : ClientServiceTeam[];
};

const ScheduleList : React.FC<Props> = ({startDate, filterSearch, projects, employees, filtersActive, teams} : Props) => {
    const apiFetch = useApiFetch();
    const {preferences} = usePreferencesState();
    const updatePreferences = useUpdatePreferences();
    const [employeeActuals, setEmployeeActuals] = useState<EmployeeActuals[] | null>(null);
    const [schedules, setSchedules] = useState<Schedule[] | null>(null);
    const [employeeSchedules, dispatchEmployeeSchedules] = useReducer(employeeSchedulesReducer, null);
    const [reasons, dispatchReasons] = useReducer(reasonsReducer, null);
    const [projectToShow, setProjectToShow] = useState<Project | null>(null);
    const {enqueueSnackbar} = useSnackbar();
    const associateIDs = useMemo(() => {
        return employees.map(employee => employee.id)
    }, [employees]);

    const currentWeek = useMemo(() => {
        const today = LocalDate.now();
        return today.dayOfWeek() === DayOfWeek.SUNDAY ? today : today.minusDays(today.dayOfWeek().value());
    }, []);

    const loadEmployeeActuals = useCallback(async () => {
        const url = new URL('/v1/scheduler/actuals', apiEndpoint);
        url.searchParams.set('dateStart', startDate.toString());
        url.searchParams.set('dateEnd', startDate.plusWeeks(7).toString());

        const response = await apiFetch(url.toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load actuals', {variant: 'error'});
        }

        const employeeActuals = await response.json() as EmployeeActuals[];
        setEmployeeActuals(employeeActuals);
    }, [apiFetch, startDate, enqueueSnackbar]);

    const {detectAndLoadMissingProjects} = useProjects();

    // Detect missing projects from actuals and load them
    useEffect(() => {
        if (employeeActuals) {
            detectAndLoadMissingProjects(employeeActuals);
        }
    }, [employeeActuals, detectAndLoadMissingProjects]);

    const loadReasons = useCallback(async () => {
        const response = await apiFetch(new URL('/v1/scheduler/reasons', apiEndpoint).toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load reasons', {variant: 'error'});
        }

        const reasons = await response.json();
        dispatchReasons({type: 'initialLoad', payload: {reasons}});
    }, [apiFetch, enqueueSnackbar]);

    const loadSchedules = useCallback(async () => {
        const url = new URL('/v1/scheduler/schedules', apiEndpoint);
        url.searchParams.set('date', startDate.toString());

        const response = await apiFetch(url.toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load schedules', {variant: 'error'});
        }

        const schedules = await response.json();
        setSchedules(schedules);

        await Promise.all([loadReasons(), loadEmployeeActuals()]);
    }, [apiFetch, startDate, loadEmployeeActuals, loadReasons, enqueueSnackbar]);

    useEffect(() => {
        setEmployeeActuals(null);
        setSchedules(null);
        dispatchReasons({type: 'initialLoad', payload: {reasons: null}});

        loadSchedules();
    }, [loadSchedules, loadReasons]);

    useEffect(() => {
        if (!schedules) {
            dispatchEmployeeSchedules({type: 'initialLoad', payload: {employeeSchedules: null}});
            return;
        }

        const employeeSchedules = compileSchedules(startDate, employees, projects, schedules, employeeActuals);
        dispatchEmployeeSchedules({type: 'initialLoad', payload: {employeeSchedules}});
    }, [startDate, projects, employees, schedules, employeeActuals]);

    const filteredEmployeeSchedules = useMemo(() => {
        if (!employeeSchedules) {
            return null;
        }

        let filteredEmployeeSchedules = employeeSchedules;
        const {favoriteEmployees, selectedPracticeAreas} = preferences.scheduler;
        const selectedTeams = new Set(preferences.scheduler.selectedTeams);

        if (filtersActive) {
            if (!selectedTeams.has(unscheduledTeamId)) {
                filteredEmployeeSchedules = filteredEmployeeSchedules.filter(
                    employeeSchedule => !(
                        employeeSchedule.schedules.length === 0
                        && employeeSchedule.employee.type === 'subcontractor'
                    )
                );
            }

            selectedTeams.delete(unscheduledTeamId);

            if (selectedTeams.size > 0) {
                const selectedCST = preferences.scheduler.selectedTeams
                    .map((teamid) => teams.find(e => e.id === teamid))
                    .filter((cst) => cst !== undefined);

                let cstAssociates : number[] = [];
                for (const cst of selectedCST) {
                    if (cst) {
                        cstAssociates = cstAssociates.concat(cst.associates.map(({associateId}) => associateId));
                        if (!preferences.scheduler.onlyCstMembers) {
                            cstAssociates = cstAssociates.concat(schedules?.filter(s => projects.find(p => p.id === s.projectId)?.teamId === cst.id)?.map(s => s.employeeId) ?? []);
                        }
                    }
                }
                //get unique id's
                cstAssociates = [...new Set(cstAssociates)];

                filteredEmployeeSchedules = filteredEmployeeSchedules.filter(
                    employeeSchedule => {
                        if (selectedTeams.has(favoritesTeamId) && favoriteEmployees.includes(employeeSchedule.employee.id)) {
                            return true;
                        }
                        return cstAssociates.includes(employeeSchedule.employee.id)
                    }
                );
            }

            if (selectedPracticeAreas.length > 0) {
                filteredEmployeeSchedules = filteredEmployeeSchedules.filter(employeeSchedule => {
                    const matchedPracticeAreas = selectedPracticeAreas.filter(
                        practiceArea => employeeSchedule.employee.practiceAreas.includes(practiceArea)
                    );

                    return matchedPracticeAreas.length === selectedPracticeAreas.length;
                });
            }
        }

        if (filterSearch.query !== '') {
            const normalizedQuery = filterSearch.query.toLocaleLowerCase();

            if (filterSearch.type === 'people') {
                filteredEmployeeSchedules = filteredEmployeeSchedules.filter(({employee}) => {
                    const name = `${employee.firstName} ${employee.lastName}`.toLocaleLowerCase();
                    return name.includes(normalizedQuery);
                });
            } else {
                filteredEmployeeSchedules = filteredEmployeeSchedules.filter(({schedules}) => schedules.some(
                    schedule => schedule.project.name.toLocaleLowerCase().includes(normalizedQuery)
                ));
            }
        }

        return filteredEmployeeSchedules;
    }, [employeeSchedules, preferences.scheduler, filtersActive, filterSearch.query, filterSearch.type, teams, schedules, projects]);

    const handleFavoriteChange = useCallback((employeeId : number, isFavorite : boolean) => {
        let newFavorites;

        if (isFavorite) {
            newFavorites = [...preferences.scheduler.favoriteEmployees, employeeId];
        } else {
            newFavorites = preferences.scheduler.favoriteEmployees.filter(favorite => favorite !== employeeId);
        }

        updatePreferences({scheduler: {favoriteEmployees: newFavorites}});
    }, [updatePreferences, preferences.scheduler.favoriteEmployees]);

    const loadingValue = 2 + (schedules ? 1 : 0);

    if (!filteredEmployeeSchedules) {
        return (
            <InlineLoadingIndicator
                variant="determinate"
                value={loadingValue / 3 * 100}
            />
        );
    }

    const highlightPhrase = filterSearch.type === 'projects' && filterSearch.query !== ''
        ? filterSearch.query
        : undefined;

    return (
        <CapacityContextProvider
            startDate={startDate}
            associateIDs={associateIDs}
        >
            <ScheduleContextProvider
                employees={employees}
                dispatchEmployeeSchedules={dispatchEmployeeSchedules}
                dispatchReasons={dispatchReasons}
                visibleEmployeeSchedules={filteredEmployeeSchedules}
            >
                <Grid container spacing={2}>
                    {filteredEmployeeSchedules.map(employeeSchedule => (
                        <Grid item key={employeeSchedule.employee.id} xs={12}>
                            <VirtualTableSchedule
                                startDate={startDate}
                                currentWeek={currentWeek}
                                employeeSchedule={employeeSchedule}
                                reasons={reasons}
                                actualsLoaded={Boolean(employeeActuals)}
                                favorite={{
                                    isFavorite: preferences.scheduler.favoriteEmployees.includes(
                                        employeeSchedule.employee.id
                                    ),
                                    onFavoriteChange: handleFavoriteChange,
                                }}
                                onProjectViewClick={project => setProjectToShow(project)}
                                highlightPhrase={highlightPhrase}
                                teams={teams}
                            />
                        </Grid>
                    ))}
                </Grid>

                {employeeSchedules && (
                    <ProjectSchedule
                        project={projectToShow}
                        startDate={startDate}
                        employeeSchedules={employeeSchedules}
                        onClose={() => setProjectToShow(null)}
                        currentWeek={currentWeek}
                    />
                )}
            </ScheduleContextProvider>
        </CapacityContextProvider>
    );
};

export default ScheduleList;
